import * as React from "react";
import { useLocation } from "wouter";
import { PageSkeleton, busyText } from "@/components/ui/skeleton";

interface PageLoaderProps {
  /**
   * The app-level wait (auth resolving, before the shell is drawn), with what
   * a screen reader hears ("Loading"). Draws the shell's frame around the
   * route's shape — see ShellSkeleton in ./route-skeleton.tsx.
   */
  message?: string;
}

/**
 * What a page shows while its CHUNK loads: that page's shape, not a spinner
 * (E2/K12) and — since 2026-09-28 — not one generic "header over a list" for
 * every route either. Andrew: "the skeleton looks nothing like the elements
 * that load in". A contacts page loaded as a list card and then became a
 * header, an action row, four figures, a search and a grid; a dashboard
 * became a cover image; a matrix became floors of tiles.
 *
 * So the route decides (./route-skeleton.tsx). The Suspense fallbacks in
 * App.tsx (and in the two settings modals) render `<PageLoader />` with no
 * message; this reads the location and draws the page as the page draws
 * itself while its data loads — the same PageHeader, the same buttons, the
 * same figures in their loading state, the same body skeleton — so the chunk
 * landing moves nothing (2026-09-29: it used to draw a hand-made guess at each
 * page from bare skeleton blocks, and the guesses drifted; see the note in
 * route-skeleton.tsx).
 *
 * The shapes live in their own chunk (route-skeleton.tsx), loaded at boot, so
 * they cost the first-load bundle nothing (scripts/bundle-budget).
 *
 * A `message` means the app-level wait (auth resolving, before the shell is
 * drawn). That used to be the generic PageSkeleton: a list card pinned to the
 * top-left of an empty window with no header bar, no sidebar and no tab bar,
 * which is the first thing every cold launch of the installed app showed —
 * and the one screen that looked like nothing in the app. It is now the
 * shell's own frame with the route's shape inside it (ShellSkeleton), and on
 * a public route — sign-in, a public quote — nothing but the canvas, since
 * those pages have no shell to stand in for.
 */
export function PageLoader({ message }: PageLoaderProps) {
  const [location] = useLocation();
  const Shapes = useRouteShapes();
  const path = location.split(/[?#]/)[0];
  if (message) {
    if (!isShellRoute(path) || !Shapes) {
      return (
        <div className="min-h-dvh" data-testid="page-loader" role="status" aria-busy="true">
          <span className="sr-only">{busyText(message)}</span>
        </div>
      );
    }
    return <Shapes.ShellSkeleton path={path} />;
  }
  if (!Shapes) return <PageSkeleton data-testid="page-loader" />;
  return <Shapes.RouteSkeleton path={path} />;
}

/** The routes AppShell wraps. Everything else is a public page with no frame. */
export function isShellRoute(path: string): boolean {
  return /^\/(projects|admin|company-admin|account)(\/|$)/.test(path) || path === "/home";
}

/* The route shapes are their own chunk, requested the moment this module is
   evaluated (at boot) and drawn once it is in. Until then — the first few
   hundred milliseconds of a cold launch, before any page chunk is being
   waited on anyway — a route falls back to the generic page skeleton, and
   the app-level wait to the bare canvas. */
type Shapes = {
  RouteSkeleton: React.ComponentType<{ path: string; cold?: boolean }>;
  ShellSkeleton: React.ComponentType<{ path: string }>;
};
let shapes: Shapes | null = null;
const listeners = new Set<() => void>();
const routeShapes =
  typeof window === "undefined"
    ? null
    : import("@/components/ui/route-skeleton")
        .then((m) => {
          shapes = { RouteSkeleton: m.RouteSkeleton, ShellSkeleton: m.ShellSkeleton };
          listeners.forEach((l) => l());
        })
        .catch(() => {
          /* the generic skeleton stays; a missing chunk must not break a fallback */
        });
void routeShapes;

function useRouteShapes() {
  return React.useSyncExternalStore(
    (on) => {
      listeners.add(on);
      return () => listeners.delete(on);
    },
    () => shapes,
    () => shapes,
  );
}
